// src/components/YourModule/GoodsReceiptModule/AddGoodsReceipt.js

import React, { useEffect, useState, useMemo, useCallback } from "react";
import Loader from "../../../loader/Loader";
import { toast } from "react-toastify";
import axios from "axios";

// ✅ Added domainAccounting
import { domain, 
  domainAccounting 
} from "../../../../security";
import Select from "react-select";
import {
  FaTimes,
  FaBoxOpen,
  FaBarcode,
  FaInfoCircle,
  FaCheckCircle,
  FaTrash,
  FaExclamationTriangle,
  // FaBan,
  // FaCheckDouble,
  FaPlus,
  FaLayerGroup,
  FaFileInvoice,
  FaTrashAlt,
} from "react-icons/fa";
import SearchableDropdown from "../../../../UI/common/SearchableDropdown";

// --- REDUX IMPORTS ---
import { useSelector } from "react-redux";
// ✅ Added selectUserID
import { selectFullName,
  selectUserID 
} from "../../../../redux/IchthusSlice";

// --- SERIAL ENTRY MODAL ---
const SerialEntryModal = ({
  isOpen,
  onClose,
  productName,
  quantity,
  expectedCount,
  serials,
  onSave,
}) => {
  const [localSerials, setLocalSerials] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const newSlots = [...serials];
      // Ensure we have exactly the expected number of slots
      if (newSlots.length < expectedCount) {
        const diff = expectedCount - newSlots.length;
        for (let i = 0; i < diff; i++) newSlots.push("");
      } else if (newSlots.length > expectedCount) {
        newSlots.splice(expectedCount);
      }
      setLocalSerials(newSlots);
    }
  }, [isOpen, quantity, expectedCount, serials]);

  if (!isOpen) return null;

  const handleSerialChange = (index, value) => {
    const updated = [...localSerials];
    updated[index] = value.toUpperCase();
    setLocalSerials(updated);
  };

  const handleConfirm = () => {
    onSave(localSerials);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="bg-indigo-600 p-4 border-b border-indigo-700 flex items-center justify-between">
          <div className="text-white">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <FaBarcode /> Serial Numbers
            </h3>
            <p className="text-xs text-indigo-100 opacity-80">{productName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-200 transition"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Qty: <b>{quantity}</b> | Rate: <b>{expectedCount / quantity}</b>
            <br />
            Please scan <b>{expectedCount}</b> serial numbers.
          </p>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-4 space-y-3 bg-white">
          {localSerials.map((serial, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <span className="text-xs font-mono text-gray-400 w-6 text-right">
                {idx + 1}.
              </span>
              <input
                type="text"
                value={serial}
                onChange={(e) => handleSerialChange(idx, e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border font-mono uppercase placeholder-gray-300"
                placeholder={`SERIAL-NO-${idx + 1}`}
                autoFocus={idx === 0}
              />
            </div>
          ))}
        </div>
        <div className="p-4 bg-gray-50 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-100 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
          >
            Save Serials
          </button>
        </div>
      </div>
    </div>
  );
};

const formatCurrency = (amount, currency = "PHP") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "PHP",
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

// --- MAIN COMPONENT ---
const AddGoodsReceipt = ({ onClose, refreshData }) => {
  const fullName = useSelector(selectFullName);
  const currentUserId = useSelector(selectUserID); // ✅ ADDED

  const [formData, setFormData] = useState({
    receiptDate: new Date().toISOString().split("T")[0],
    purchaseOrderHeaderId: "",
    manualGrNumber: "",
    receivedBy: "",
    notes: "",
    vendorId: null,
    goodsReceiptLines: [],
    goodReceiptMainDetailed: [],
  });

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [productsMap, setProductsMap] = useState({});
  const [conversions, setConversions] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // For the Modal
  const [serialModalOpen, setSerialModalOpen] = useState(false);
  const [activeLineIndex, setActiveLineIndex] = useState(null);

  const inputStyles =
    "block w-full p-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white";
  const labelStyles = "block text-sm font-semibold text-gray-700 mb-1";

  

  const [purchaseOrdersByVendor, setPurchaseOrdersByVendor] = useState([])

  const fetchDataPOByVendor = useCallback(async (vendorId) => {
    try {
      const response = await axios.get(`${domain}/api/PurchaseOrderHeaders/vendor/${vendorId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return []
    }
  }, []);

  useEffect(() => {
    if(formData.vendorId){
      const fetch = async () => {
        setIsLoading(true)
        const _data = await fetchDataPOByVendor(formData.vendorId)
        setPurchaseOrdersByVendor(_data)
        setIsLoading(false)
      }

      fetch();
    }
  }, [fetchDataPOByVendor, formData.vendorId])

  useEffect(() => {
    if (fullName) {
      setFormData((prev) => ({ ...prev, receivedBy: fullName }));
    }
  }, [fullName]);

  // 1. FETCH DATA
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [poRes, vendorsRes, productRes, uomRes] = await Promise.all([
          axios.get(`${domain}/api/PurchaseOrderHeaders`),
          axios.get(`${domain}/api/Vendors`),
          axios.get(`${domain}/api/Products`),
          axios.get(`${domain}/api/UomConversions`),
        ]);

        // Filter for active POs
        const activePOs = poRes.data.filter((po) => {
          if (po.status === "Closed" || po.status === "Cancelled") return false;

          const lines =
            po.purchaseOrderLineItems || po.PurchaseOrderLineItems || [];
          if (lines.length > 0) {
            return lines.some((item) => {
              const received =
                item.receivedQuantity || item.ReceivedQuantity || 0;
              const rejected =
                item.rejectedQuantity || item.RejectedQuantity || 0;
              const ordered =
                item.orderedQuantity ||
                item.OrderedQuantity ||
                item.quantity ||
                item.Quantity ||
                0;
              return ordered - received - rejected > 0;
            });
          }
          return true;
        });

        setPurchaseOrders(activePOs);
        setVendors(vendorsRes.data);
        setConversions(uomRes.data);

        // Process Products
        const options = [];
        const map = {};

        productRes.data.forEach((item) => {
          map[item.id] = item;
          options.push({
            value: item.id,
            label: `${item.productName} - ${item.itemCode}`,
            itemCode: item.itemCode,
            productName: item.productName,
            hasSerial: item.hasSerial,
            baseUomId: item.baseUomId,
          });
        });

        setProductOptions(options);
        setProductsMap(map);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // 2. PO SELECTION & INITIALIZATION
  useEffect(() => {
    if (formData.purchaseOrderHeaderId && Object.keys(productsMap).length > 0) {
      const targetId = parseInt(formData.purchaseOrderHeaderId);
      const po = purchaseOrdersByVendor.find((p) => p.id === targetId);
      setSelectedPO(po);

      if (po) {
        const rawLines =
          po.purchaseOrderLineItems || po.PurchaseOrderLineItems || [];

        const initialLines = rawLines.map((item) => {
          const lineItemId = item.id || item.Id;
          const productId = item.productId || item.ProductId;

          const received = item.receivedQuantity || item.ReceivedQuantity || 0;
          const rejected = item.rejectedQuantity || item.RejectedQuantity || 0;
          const ordered =
            item.orderedQuantity ||
            item.OrderedQuantity ||
            item.quantity ||
            item.Quantity ||
            0;
          const uomId = item.unitOfMeasureId || item.UnitOfMeasureId;
          const productName = item.productName || item.ProductName;
          const uomName = item.unitOfMeasure || item.UnitOfMeasure;

          let trueRemaining = ordered - received - rejected;
          if (trueRemaining < 0) trueRemaining = 0;

          const productDetails = productId ? productsMap[productId] : null;
          const hasSerial = productDetails ? productDetails.hasSerial : false;

          let rate = 1;
          if (productDetails && uomId && uomId !== productDetails.baseUomId) {
            const foundConv = conversions.find(
              (c) =>
                c.productId === productId &&
                c.fromUomId === uomId &&
                c.toUomId === productDetails.baseUomId,
            );
            if (foundConv) rate = foundConv.conversionRate;
          }

          return {
            purchaseOrderLineItemId: lineItemId,
            productId: productId,
            productName: productName,
            remainingQuantity: trueRemaining,
            orderedQuantity: ordered,
            unitOfMeasure: uomName,
            conversionRate: rate,
            isCustom: !productId,
            hasSerial: hasSerial,
            quantityReceived: 0,
            serialNumbers: [],
          };
        });

        setFormData((prev) => ({ ...prev, goodsReceiptLines: initialLines }));
      }
    } else {
      setSelectedPO(null);
      setFormData((prev) => ({ ...prev, goodsReceiptLines: [] }));
    }
  }, [
    formData.purchaseOrderHeaderId,
    purchaseOrdersByVendor,
    productsMap,
    conversions,
  ]);

  const handleHeaderChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handlePOSelect = (value) => {
    const cleanValue =
      typeof value === "object" && value !== null
        ? value.value || value.id
        : value;
    setFormData({ ...formData, purchaseOrderHeaderId: cleanValue });
  };

  const handleVendorSelect = (value) => {
    const cleanValue =
      typeof value === "object" && value !== null
        ? value.value || value.id
        : value;
    setFormData({ ...formData, vendorId: cleanValue });
  };

  const handleProductMapping = (index, selectedOption) => {
    const lines = [...formData.goodsReceiptLines];
    if (selectedOption) {
      lines[index].productId = selectedOption.value;
      lines[index].hasSerial = selectedOption.hasSerial;
      lines[index].conversionRate = 1;
      if (!selectedOption.hasSerial) {
        lines[index].serialNumbers = [];
      }
    } else {
      lines[index].productId = null;
      lines[index].hasSerial = false;
      lines[index].serialNumbers = [];
    }
    setFormData({ ...formData, goodsReceiptLines: lines });
  };

  /* const handleQuantityChange = (index, value) => {
    const lines = [...formData.goodsReceiptLines];
    const max = lines[index].remainingQuantity;
    let newQty = value === "" ? "" : parseInt(value) || 0;

    if (newQty < 0) newQty = 0;
    if (max === 0) newQty = 0;

    if (newQty > max) {
      toast.warn(`Cannot receive more than ${max} (Remaining Order).`);
      newQty = max;
    }

    lines[index].quantityReceived = newQty;

    const expectedSerials = newQty * lines[index].conversionRate;
    if (lines[index].hasSerial) {
      const currentSerials = lines[index].serialNumbers;
      if (expectedSerials < currentSerials.length) {
        lines[index].serialNumbers = currentSerials.slice(0, expectedSerials);
      }
    }

    setFormData({ ...formData, goodsReceiptLines: lines });
  }; */

  const handleQuantityChange = (poIndex, lineIndex, value) => {
    // 1. Deep copy the nested array structure
    const updatedDetailed = [...formData.goodReceiptMainDetailed];
    const po = { ...updatedDetailed[poIndex] };
    const lines = [...po.goodReceiptMainDetailedLine];
    const line = { ...lines[lineIndex] };

    const max = line.remainingQuantity;
    let newQty = value === "" ? "" : parseInt(value) || 0;

    // Validation
    if (newQty < 0) newQty = 0;
    if (max === 0) newQty = 0;

    if (newQty > max) {
      toast.warn(`Cannot receive more than ${max} for PO ${po.poNumber}`);
      newQty = max;
    }

    // Update the specific line
    line.quantityReceived = newQty;

    // Handle Serial Numbers logic
    const expectedSerialsCount = newQty * (line.conversionRate || 1);
    if (line.hasSerial) {
      const currentSerials = line.serialNumbers || [];
      if (expectedSerialsCount < currentSerials.length) {
        // If quantity decreased, trim the serial numbers list
        line.serialNumbers = currentSerials.slice(0, expectedSerialsCount);
      } else if (expectedSerialsCount > currentSerials.length) {
        // If quantity increased, we need to ensure the array has enough slots
        // (Optional: add empty strings for new slots)
        const additionalSlots = Array(expectedSerialsCount - currentSerials.length).fill("");
        line.serialNumbers = [...currentSerials, ...additionalSlots];
      }
    }

    // Re-assemble the nested state
    lines[lineIndex] = line;
    po.goodReceiptMainDetailedLine = lines;
    updatedDetailed[poIndex] = po;

    setFormData({ 
      ...formData, 
      goodReceiptMainDetailed: updatedDetailed 
    });
  };

  const openSerialModal = (index) => {
    setActiveLineIndex(index);
    setSerialModalOpen(true);
  };

  const handleSerialSave = (updatedSerials) => {
    const lines = [...formData.goodsReceiptLines];
    if (activeLineIndex !== null) {
      lines[activeLineIndex].serialNumbers = updatedSerials;
      setFormData({ ...formData, goodsReceiptLines: lines });
    }
  };

  /* const handleRevertLine = (index) => {
    const lines = [...formData.goodsReceiptLines];
    lines[index].quantityReceived = 0;
    lines[index].serialNumbers = [];
    setFormData({ ...formData, goodsReceiptLines: lines });
  }; */

  const handleRevertLine = (poIndex, lineIndex) => {
    const updatedDetailed = [...formData.goodReceiptMainDetailed];
    const po = { ...updatedDetailed[poIndex] };
    const lines = [...po.goodReceiptMainDetailedLine];

    // Reset the specific line values
    lines[lineIndex] = {
      ...lines[lineIndex],
      quantityReceived: 0,
      serialNumbers: []
    };

    po.goodReceiptMainDetailedLine = lines;
    updatedDetailed[poIndex] = po;

    setFormData({ 
      ...formData, 
      goodReceiptMainDetailed: updatedDetailed 
    });
  };

  /* const fetchPOById = useCallback(async (id) => {
    try {
      const response = await axios.get(`${domain}/api/PurchaseOrderHeaders/${id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, []); */

  /* const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // --- 1. Validate Header ---
    if (!formData.purchaseOrderHeaderId) {
      toast.error("Please select a valid Purchase Order.");
      setIsLoading(false);
      return;
    }

    try {
      const validLines = formData.goodsReceiptLines.filter(
        (l) => l.quantityReceived > 0,
      );

      if (validLines.length === 0) {
        toast.warn("Please enter a received quantity for at least one item.");
        setIsLoading(false);
        return;
      }

      // --- 2. Validate Lines ---
      for (const line of validLines) {
        if (!line.productId) {
          toast.error(
            `Please select a System Product for custom item: "${line.productName}"`,
          );
          setIsLoading(false);
          return;
        }
        if (!line.purchaseOrderLineItemId) {
          toast.error(
            `System Error: Line Item ID missing for ${line.productName}. Refresh and try again.`,
          );
          setIsLoading(false);
          return;
        }
        if (line.hasSerial) {
          const expected = line.quantityReceived * line.conversionRate;
          if (line.serialNumbers.length !== expected) {
            toast.error(
              `Error on ${line.productName}: Expected ${expected} serial numbers, got ${line.serialNumbers.length}.`,
            );
            setIsLoading(false);
            return;
          }
        }
      }

      // --- 3. Construct Payload ---
      const submitPayload = {
        receiptDate: new Date(formData.receiptDate).toISOString(),
        purchaseOrderHeaderId: parseInt(formData.purchaseOrderHeaderId),
        manualGrNumber: formData.manualGrNumber || null,
        receivedBy: formData.receivedBy || "Unknown",
        notes: formData.notes,
        goodsReceiptLines: validLines.map((line) => ({
          purchaseOrderLineItemId: parseInt(line.purchaseOrderLineItemId),
          productId: parseInt(line.productId),
          quantityReceived: parseInt(line.quantityReceived),
          serialNumbers: line.hasSerial
            ? line.serialNumbers.filter((s) => s.trim() !== "")
            : [],
        })),
      };

      const res = await axios.post(
        `${domain}/api/GoodsReceipts`,
        submitPayload,
      );

      // --- 4. AUTO-SYNC TO ACCOUNTING ---
      try {
        const grNumberStr =
          formData.manualGrNumber ||
          res.data?.receiptNumber ||
          res.data?.data?.receiptNumber ||
          `GR-${Date.now()}`;

        let totalAmount = 0;
        const goodReceiptDetails = validLines.map((line) => {
          const poLineItems =
            selectedPO.purchaseOrderLineItems ||
            selectedPO.PurchaseOrderLineItems ||
            [];
          const poLine =
            poLineItems.find(
              (pl) =>
                (pl.id || pl.Id) === parseInt(line.purchaseOrderLineItemId) ||
                (pl.productId || pl.ProductId) === line.productId,
            ) || {};

          const unitPrice = Number(poLine.unitPrice || poLine.UnitPrice || 0);
          const orderedQty = line.orderedQuantity || 0;
          const receivedQty = line.quantityReceived || 0;
          const remainingQty = Math.max(
            0,
            (line.remainingQuantity || 0) - receivedQty,
          );
          const subTotal = receivedQty * unitPrice;

          totalAmount += subTotal;

          const productDetails = productsMap[line.productId];
          const itemCodeStr = productDetails
            ? productDetails.itemCode
            : line.productName;

          return {
            id: 0,
            goodsReceiptHeaderId: 0,
            itemCode: itemCodeStr || "Unknown",
            orderedQty: orderedQty,
            receivedQty: receivedQty,
            remainingQty: remainingQty,
            unitPrice: unitPrice,
            subTotal: subTotal,
            taxationCodeId: 0,
            totalDiscount: 0,
            taxAmount: 0,
            totalAmount: subTotal,
          };
        });

        const _po = await fetchPOById(parseInt(formData.purchaseOrderHeaderId))

        const grAccountingPayload = [
          {
            goodReceiptDetails: goodReceiptDetails,
            grNumber: grNumberStr,
            purchaseOrderPONumber: _po.poNumber ?? "",
            amount: totalAmount,
            grDate: new Date(formData.receiptDate).toISOString(),
            uploadedFile: "",
            notes: formData.notes || "",
            status: "Closed",
            createdDate: new Date().toISOString(),
            createdBy: currentUserId || 0,
          },
        ];

        console.log('Final bulkData:', JSON.stringify(grAccountingPayload, null, 2));

        //temporary disabled
        // await axios.post(
        //   `${domainAccounting}/api/GoodReceipt/bulk`,
        //   grAccountingPayload,
        // );
        console.log(`Auto-synced GR ${grNumberStr} to Accounting`);
      } catch (accErr) {
        console.error("Failed to auto-sync GR to Accounting:", accErr);
      }
      // ----------------------------------

      toast.success("Goods Receipt & Batches created successfully!");
      refreshData();
      onClose();
    } catch (error) {
      console.error(error);
      const msg =
        error.response?.data?.message || "Failed to create Goods Receipt.";
      const detailedMsg = error.response?.data?.error || msg;
      toast.error(detailedMsg);
    } finally {
      setIsLoading(false);
    }
  }; */

  const handleAccountingSubmit = async (_data) => {
    setIsLoading(true);

    const amount = _data.goodReceiptMainDetailed?.reduce(
        (sum, item) => sum + (Number(item.grandTotal) || 0),
        0
      ) || 0;

    const finalData = [
      {
        goodReceiptDetails: _data.goodReceiptMainDetailed?.map((item) => ({
          purchaseOrderPONumber: item.purchaseOrderNumber,

          goodReceiptDetailLines: item.goodReceiptMainDetailedLine?.map((line) => ({
            itemCode: line.product?.itemCode,
            orderedQty: line.purchaseOrderLineItem?.quantity,
            receivedQty: line.quantityReceived,
            remainingQty: line.quantityRemaining ?? 0,
            unitPrice: line.purchaseOrderLineItem?.unitPrice,
            subTotal: line?.subTotal,
            taxationCodeId: 0,
            totalDiscount: line?.totalDiscount,
            taxAmount: line?.taxAmount,
            totalAmount: line?.totalAmount
          }))
        })),

        grNumber: _data.receiptNumber, 
        manualGRNumber: _data.manualGrNumber ?? null,
        vendorId: _data.vendor?.vendorCode,
        amount: amount,
        grDate: _data.receiptDate,
        uploadedFile: "",
        notes: _data?.notes,
        status: "Open",
        createdDate: new Date().toISOString(),
        createdBy: currentUserId || 0,
      }
    ];

    console.log({finalData})

    try {
      await axios.post(
        `${domainAccounting}/api/GoodReceipt/bulk`,
        finalData,
      );

    } catch (error) {
      console.error(error);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // --- 1. Validate Header ---
    if (formData.goodReceiptMainDetailed.length <= 0) {
      toast.error("Please select a valid Purchase Order.");
      setIsLoading(false);
      return;
    }

    const hasAtLeastOneItem = formData.goodReceiptMainDetailed.some(item => 
      item.goodReceiptMainDetailedLine.some(line => line.quantityReceived > 0)
    );

    if (!hasAtLeastOneItem) {
      toast.error("Please input at least 1 received item.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${domain}/api/GoodsReceiptMain`,
        formData,
      );

      // --- 4. AUTO-SYNC TO ACCOUNTING ---
      // try {
      //   const grNumberStr =
      //     formData.manualGrNumber ||
      //     res.data?.receiptNumber ||
      //     res.data?.data?.receiptNumber ||
      //     `GR-${Date.now()}`;

      //   let totalAmount = 0;
      //   const goodReceiptDetails = validLines.map((line) => {
      //     const poLineItems =
      //       selectedPO.purchaseOrderLineItems ||
      //       selectedPO.PurchaseOrderLineItems ||
      //       [];
      //     const poLine =
      //       poLineItems.find(
      //         (pl) =>
      //           (pl.id || pl.Id) === parseInt(line.purchaseOrderLineItemId) ||
      //           (pl.productId || pl.ProductId) === line.productId,
      //       ) || {};

      //     const unitPrice = Number(poLine.unitPrice || poLine.UnitPrice || 0);
      //     const orderedQty = line.orderedQuantity || 0;
      //     const receivedQty = line.quantityReceived || 0;
      //     const remainingQty = Math.max(
      //       0,
      //       (line.remainingQuantity || 0) - receivedQty,
      //     );
      //     const subTotal = receivedQty * unitPrice;

      //     totalAmount += subTotal;

      //     const productDetails = productsMap[line.productId];
      //     const itemCodeStr = productDetails
      //       ? productDetails.itemCode
      //       : line.productName;

      //     return {
      //       id: 0,
      //       goodsReceiptHeaderId: 0,
      //       itemCode: itemCodeStr || "Unknown",
      //       orderedQty: orderedQty,
      //       receivedQty: receivedQty,
      //       remainingQty: remainingQty,
      //       unitPrice: unitPrice,
      //       subTotal: subTotal,
      //       taxationCodeId: 0,
      //       totalDiscount: 0,
      //       taxAmount: 0,
      //       totalAmount: subTotal,
      //     };
      //   });

      //   const _po = await fetchPOById(parseInt(formData.purchaseOrderHeaderId))

      //   const grAccountingPayload = [
      //     {
      //       goodReceiptDetails: goodReceiptDetails,
      //       grNumber: grNumberStr,
      //       purchaseOrderPONumber: _po.poNumber ?? "",
      //       amount: totalAmount,
      //       grDate: new Date(formData.receiptDate).toISOString(),
      //       uploadedFile: "",
      //       notes: formData.notes || "",
      //       status: "Closed",
      //       createdDate: new Date().toISOString(),
      //       createdBy: currentUserId || 0,
      //     },
      //   ];

      //   console.log('Final bulkData:', JSON.stringify(grAccountingPayload, null, 2));

      //   //temporary disabled
      //   // await axios.post(
      //   //   `${domainAccounting}/api/GoodReceipt/bulk`,
      //   //   grAccountingPayload,
      //   // );
      //   console.log(`Auto-synced GR ${grNumberStr} to Accounting`);
      // } catch (accErr) {
      //   console.error("Failed to auto-sync GR to Accounting:", accErr);
      // }
      // ----------------------------------

      await handleAccountingSubmit(response.data)
      toast.success("Goods Receipt & Batches created successfully!");
      refreshData();
      onClose();
    } catch (error) {
      console.error(error);
      const msg =
        error.response?.data?.message || "Failed to create Goods Receipt.";
      const detailedMsg = error.response?.data?.error || msg;
      toast.error(detailedMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const autoBatchNamePreview = useMemo(() => {
    if (!selectedPO) return "...";
    const dateStr = formData.receiptDate.replace(/-/g, "");
    return `${selectedPO.poNumber}-${selectedPO.vendorId}-${dateStr}`;
  }, [selectedPO, formData.receiptDate]);

  const poDropdownOptions = useMemo(() => {
    return purchaseOrders.map((po) => ({
      ...po,
      displayLabel: `${po.poNumber} | Location: ${po.location || "N/A"}`,
    }));
  }, [purchaseOrders]);

  const handleAddPO = () => {
    if (!selectedPO) return;

    // 1. Add to the "Added POs" list
    setFormData({
      ...formData,
      goodReceiptMainDetailed: [
        ...formData.goodReceiptMainDetailed,
        {
          purchaseOrderHeaderId: selectedPO.id,
          poNumber: selectedPO.displayLabel || selectedPO.poNumber,
          purchaseOrders: selectedPO, // Stored for restoration if removed
          grandTotal: selectedPO?.grandTotal ?? 0,
          
          // Map the line items and inject the new key + initial values
          goodReceiptMainDetailedLine: selectedPO.purchaseOrderLineItems?.filter(i => i.remainingQuantity > 0)?.map((line) => ({
            ...line,
            purchaseOrderLineItemId: line.id, // Setting the key as requested
            quantityReceived: line.remainingQuantity,              // Initializing receiving qty
            serialNumbers: [],                // Initializing serials array
          })),
        },
      ],
    });

    // 2. Remove from the available dropdown options
    setPurchaseOrdersByVendor((prev) => 
      prev.filter((item) => item.id !== selectedPO.id)
    );

    // 3. Reset selection
    setSelectedPO(null);
    // Also clear the specific field in formData used by the dropdown if necessary
    setFormData(prev => ({ ...prev, purchaseOrderHeaderId: "" }));
  };

  const handleRemovePO = (id) => {
    // 1. Find the full PO object from our selected list before removing it
    const itemToRestore = formData.goodReceiptMainDetailed.find(
      (item) => item.purchaseOrderHeaderId === id
    );

    if (!itemToRestore) return;

    // 2. Remove from the "Added POs" list
    setFormData({
      ...formData,
      goodReceiptMainDetailed: formData.goodReceiptMainDetailed.filter(
        (item) => item.purchaseOrderHeaderId !== id
      ),
    });

    // 3. Add the PO object back into the dropdown options
    setPurchaseOrdersByVendor((prev) => [...prev, 
      {...itemToRestore.purchaseOrders, 
        displayLabel: `${itemToRestore.purchaseOrders?.poNumber} | Location: ${itemToRestore.purchaseOrders?.location ?? "N/A"}`
      }
    ]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 p-4 backdrop-blur-sm">
      {/* SERIAL MODAL RENDER */}
      {activeLineIndex !== null &&
        formData.goodsReceiptLines[activeLineIndex] && (
          <SerialEntryModal
            isOpen={serialModalOpen}
            onClose={() => setSerialModalOpen(false)}
            onSave={handleSerialSave}
            productName={
              formData.goodsReceiptLines[activeLineIndex].productName
            }
            quantity={
              formData.goodsReceiptLines[activeLineIndex].quantityReceived
            }
            expectedCount={
              formData.goodsReceiptLines[activeLineIndex].quantityReceived *
              formData.goodsReceiptLines[activeLineIndex].conversionRate
            }
            serials={formData.goodsReceiptLines[activeLineIndex].serialNumbers}
          />
        )}

      <div className="relative flex h-full max-h-[95vh] w-full max-w-6xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        {/* HEADER */}
        <div className="flex flex-shrink-0 items-center justify-between border-b bg-indigo-50 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600 rounded-lg text-white">
              <FaBoxOpen className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                New Goods Receipt
              </h2>
              <p className="text-sm text-gray-500">
                Receive inventory against a Purchase Order
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-red-500 transition hover:bg-red-50 rounded-full"
          >
            <FaTimes className="h-6 w-6" />
          </button>
        </div>

        {isLoading && <Loader />}

        <form
          className="flex-grow overflow-y-auto p-6 space-y-8 bg-white"
          onSubmit={handleFormSubmit}
        >
          {/* SECTION 1: HEADER INPUTS */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label htmlFor="receiptDate" className={labelStyles}>
                Receipt Date *
              </label>
              <input
                id="receiptDate"
                type="date"
                value={formData.receiptDate}
                onChange={handleHeaderChange}
                required
                className={inputStyles}
              />
            </div>

            <div className="z-10 relative hidden">
              <label htmlFor="purchaseOrderHeaderId" className={labelStyles}>
                Purchase Order *
              </label>
              <SearchableDropdown
                options={poDropdownOptions}
                labelKey="displayLabel"
                valueKey="id"
                placeholder="-- Select Pending PO --"
                value={
                  formData.purchaseOrderHeaderId
                    ? parseInt(formData.purchaseOrderHeaderId)
                    : ""
                }
                onChange={handlePOSelect}
              />
            </div>

            <div className="z-10 relative">
              <label htmlFor="vendorId" className={labelStyles}>
                Vendor *
              </label>
              <SearchableDropdown
                options={vendors}
                labelKey="displayLabel"
                valueKey="id"
                placeholder="-- Select Vendor --"
                value={
                  formData.vendorId
                    ? parseInt(formData.vendorId)
                    : ""
                }
                onChange={handleVendorSelect}
              />
            </div>

            <div>
              <label htmlFor="manualGrNumber" className={labelStyles}>
                Manual GR Number
              </label>
              <input
                type="text"
                id="manualGrNumber"
                value={formData.manualGrNumber}
                onChange={handleHeaderChange}
                className={inputStyles}
                placeholder="Ex. DEL-12345 (Optional)"
              />
            </div>

            <div>
              <label htmlFor="receivedBy" className={labelStyles}>
                Received By
              </label>
              <input
                type="text"
                id="receivedBy"
                value={formData.receivedBy}
                className="bg-gray-100 cursor-not-allowed block w-full p-2.5 border border-gray-300 rounded-lg shadow-sm text-sm text-gray-600"
                disabled
              />
            </div>
          </div>
          <div>
            <label className={labelStyles}>Notes</label>
            <textarea
              id="notes"
              rows={2}
              className={`${inputStyles} resize-none`}
              placeholder="Comments..."
              value={formData.notes}
              onChange={handleHeaderChange}
            ></textarea>
          </div>

          {/* PO Management Container */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-6">
            <div className="flex items-center gap-2 mb-4 text-slate-800">
              <FaLayerGroup className="text-indigo-500" />
              <h3 className="font-bold">Purchase Order Selection</h3>
            </div>

            {/* Input Row */}
            <div className="flex flex-col md:flex-row gap-3 items-end bg-white p-4 rounded-lg shadow-sm border border-slate-100">
              <div className="flex-1 w-full relative">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 ml-1">
                  Available Purchase Orders
                </label>
                <SearchableDropdown
                  options={purchaseOrdersByVendor}
                  labelKey="displayLabel"
                  valueKey="id"
                  placeholder={purchaseOrdersByVendor.length === 0 ? "No POs available" : "-- Select Pending PO --"}
                  value={selectedPO?.id}
                  onChange={handlePOSelect}
                />
              </div>
              
              <button
                type="button"
                onClick={handleAddPO}
                disabled={isLoading || !selectedPO}
                className="h-[42px] px-6 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-lg transition-all flex items-center gap-2 shadow-lg shadow-indigo-100 disabled:shadow-none whitespace-nowrap"
              >
                <FaPlus /> Add to Receipt
              </button>
            </div>

            {/* Selected POs Display */}
            <div className="mt-6">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                POs included in this receipt: {formData.goodReceiptMainDetailed.length}
              </h4>
              
              {formData.goodReceiptMainDetailed.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {formData.goodReceiptMainDetailed.map((item) => (
                    <div 
                      key={item.purchaseOrderHeaderId}
                      className="flex items-center justify-between bg-white border border-indigo-100 rounded-lg p-3 shadow-sm hover:border-indigo-300 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-indigo-50 p-2 rounded text-indigo-600">
                          <FaFileInvoice />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium">PO Number</p>
                          <p className="text-sm font-bold text-slate-800">
                            {item.poNumber || `ID: ${item.purchaseOrderHeaderId}`}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => handleRemovePO(item.purchaseOrderHeaderId)}
                        className="text-slate-300 hover:text-red-500 p-2 rounded-md hover:bg-red-50 transition-all"
                        title="Remove PO"
                      >
                        <FaTrashAlt size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-200 rounded-xl py-8 flex flex-col items-center justify-center">
                  <p className="text-slate-400 text-sm">No Purchase Orders selected yet.</p>
                </div>
              )}
            </div>
          </div>

          {selectedPO && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hidden items-start gap-3">
              <FaInfoCircle className="text-blue-500 mt-1" />
              <div>
                <h4 className="font-semibold text-blue-900 text-sm">
                  Automated Inventory Processing
                </h4>
                <p className="text-xs text-blue-700 mt-1">
                  Batch:{" "}
                  <span className="font-mono bg-blue-100 px-2 py-0.5 rounded ml-1 text-blue-800 font-bold">
                    {autoBatchNamePreview}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* SECTION 2: LINE ITEMS TABLE */}
          {formData.goodReceiptMainDetailed?.length > 0 && (
            <div className="border rounded-xl overflow-hidden shadow-sm h-[400px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 relative">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider w-[40%]">
                      Product Information
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-32">
                      Unit Price
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-32">
                      Ord / Rem
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-indigo-700 uppercase tracking-wider w-40">
                      Rec. Qty
                    </th>
                    <th className="hidden px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Serials / Config
                    </th>
                    <th className="hidden px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider w-20">
                      Revert
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-32">
                      Total Amount
                    </th>
                  </tr>
                </thead>
                {/* Table Body */}
                <tbody className="bg-white divide-y divide-gray-200">
                  {formData.goodReceiptMainDetailed.length > 0 ? (
                    formData.goodReceiptMainDetailed.flatMap((po, poIndex) => 
                      po.goodReceiptMainDetailedLine.map((line, lineIndex) => {
                        const isFullyReceived = line.remainingQuantity <= 0;

                        const totalAmount = line.quantityReceived * line.unitPrice;
                        
                        return (
                          <tr
                            key={`${po.poNumber}-${line.id}-${lineIndex}`} // Unique Key
                            className={
                              isFullyReceived
                                ? "bg-gray-50 opacity-60"
                                : line.quantityReceived > 0
                                ? "bg-indigo-50/40"
                                : "hover:bg-gray-50 transition-colors"
                            }
                          >
                            {/* Product & PO Info */}
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">{line.productName}</span>
                                  {/* PO Badge so user knows which PO this line belongs to */}
                                  <span className="text-[10px] font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded shadow-sm">
                                    {po.poNumber}
                                  </span>
                                </div>
                                
                                {!line.isCustom ? (
                                  <span className="text-[11px] text-green-600 flex items-center gap-1">
                                    <FaCheckCircle size={10} /> ID: {line.productId}
                                    {line.hasSerial && (
                                      <span className="text-indigo-600 font-bold ml-1 px-1 bg-indigo-50 rounded">
                                        (Serial Required)
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <div className="w-full mt-2">
                                    <label className="text-[10px] uppercase font-bold text-orange-600 flex items-center gap-1 mb-1">
                                      <FaExclamationTriangle /> Map to System Product
                                    </label>
                                    <Select
                                      isDisabled={isFullyReceived}
                                      menuPortalTarget={document.body}
                                      options={productOptions}
                                      placeholder="Search..."
                                      className="text-xs"
                                      value={productOptions.find(opt => opt.value === line.productId) || null}
                                      onChange={(option) => handleProductMapping(poIndex, lineIndex, option)}
                                    />
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Unit Price */}
                            <td className="px-6 py-4 text-center">
                              <span className="text-sm font-bold text-gray-800">{formatCurrency(line.unitPrice, po.currency)}</span>
                            </td>

                            {/* Balances */}
                            <td className="px-6 py-4 text-center">
                              <div className="inline-flex flex-col items-center">
                                <div className="flex items-baseline gap-1">
                                  <span className="text-sm font-bold text-gray-800">{line.remainingQuantity}</span>
                                  <span className="text-[10px] text-gray-400 uppercase">Remaining</span>
                                </div>
                                <div className="text-[10px] text-gray-400 italic">
                                  of {line.orderedQuantity} {line.unitOfMeasure}
                                </div>
                              </div>
                            </td>

                            {/* Receiving Input */}
                            <td className="px-6 py-4">
                              <div className="flex justify-center">
                                <input
                                  type="number"
                                  min="0"
                                  max={line.remainingQuantity}
                                  disabled={isFullyReceived}
                                  value={line.quantityReceived}
                                  onChange={(e) => handleQuantityChange(poIndex, lineIndex, e.target.value)}
                                  className={`w-20 text-center text-sm rounded-md border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 font-bold ${
                                    isFullyReceived ? "bg-gray-100 text-gray-400" : "bg-white text-indigo-700"
                                  }`}
                                />
                              </div>
                            </td>

                            {/* Total Amount */}
                            <td className="px-6 py-4 text-center">
                              <span className="text-sm font-bold text-gray-800">{formatCurrency(totalAmount, po.currency)}</span>
                            </td>

                            {/* Serials */}
                            <td className="px-6 py-4 hidden">
                              {line.hasSerial ? (
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openSerialModal(poIndex, lineIndex)}
                                    disabled={isFullyReceived || line.quantityReceived <= 0}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold transition-all border
                                      ${line.quantityReceived > 0 
                                        ? "border-indigo-200 bg-white text-indigo-600 hover:bg-indigo-50 shadow-sm" 
                                        : "border-gray-200 bg-gray-50 text-gray-400 pointer-events-none"}
                                    `}
                                  >
                                    <FaBarcode size={14} />
                                    {line.serialNumbers?.filter(s => s).length || 0} / {line.quantityReceived}
                                  </button>
                                </div>
                              ) : (
                                <div className="text-center text-gray-300 italic text-[10px]">N/A</div>
                              )}
                            </td>

                            {/* Row Actions */}
                            <td className="px-6 py-4 text-right hidden">
                              {line.quantityReceived > 0 && !isFullyReceived && (
                                <button
                                  type="button"
                                  onClick={() => handleRevertLine(poIndex, lineIndex)}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                  title="Reset Quantity"
                                >
                                  <FaTrash size={12} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center text-gray-400">
                          <FaFileInvoice size={32} className="mb-2 opacity-20" />
                          <p className="text-sm italic">Please add a Purchase Order to see items.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </form>

        <div className="flex-shrink-0 border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition shadow-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleFormSubmit}
            disabled={isLoading || formData.goodReceiptMainDetailed?.length <= 0}
            className="px-5 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition shadow-md disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            Confirm Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddGoodsReceipt;
